import axios, { type AxiosError } from "axios";

import type { UsersAPI } from "generated/types";
import { type ApiRequestError, RequestError } from "models/error/Error";

import { ERROR_TYPES } from "constants/error-constants";

import cookieStorage from "persistence";
import { handlingResponse, logError } from "repositories/utils";

export const fetchEmailInviteBundleDetails = async (
  emailInviteBundleId: string,
): Promise<UsersAPI.EmailInviteBundleDetails> => {
  try {
    const response = await axios.get(`/api/invite/email-bundle/${emailInviteBundleId}`, {
      headers: { Authorization: cookieStorage.getToken() },
    });
    return handlingResponse<UsersAPI.EmailInviteBundleDetails>(
      [200],
      "Error fetching email invite bundle details",
    )(response);
  } catch (error) {
    const { message, status, errCode } = logError(error as AxiosError<ApiRequestError>);
    throw new RequestError({
      message,
      status,
      errCode,
      type: ERROR_TYPES.GET_EMAIL_INVITE_BUNDLE_DETAILS_FAILED,
    });
  }
};

export const fetchLinkInviteDetails = async (
  linkInviteId: string,
): Promise<UsersAPI.InviteLinkDetails> => {
  try {
    const response = await axios.get(`/api/invite/invitation-link/${linkInviteId}`, {
      headers: { Authorization: cookieStorage.getToken() },
    });
    return handlingResponse<UsersAPI.InviteLinkDetails>(
      [200],
      "Error fetching link invite details",
    )(response);
  } catch (error) {
    const { message, status, errCode } = logError(error as AxiosError<ApiRequestError>);
    throw new RequestError({
      message,
      status,
      errCode,
      type: ERROR_TYPES.GET_EMAIL_INVITE_BUNDLE_DETAILS_FAILED,
    });
  }
};

// Request to create a new invitation link
export const createInvitationLink = async (
  payload: UsersAPI.CreateInvitationLinkRequest,
): Promise<UsersAPI.CreateInvitationLinkResponse> => {
  try {
    const response = await axios.post("/api/invite/invitation-link", payload, {
      headers: { Authorization: cookieStorage.getToken() },
    });
    return handlingResponse<UsersAPI.CreateInvitationLinkResponse>(
      [200],
      "Error creating invitation link",
    )(response);
  } catch (error) {
    const { message, status, errCode } = logError(error as AxiosError<ApiRequestError>);
    throw new RequestError({
      message,
      status,
      errCode,
      type: ERROR_TYPES.CREATE_INVITATION_LINK_FAILED,
    });
  }
};

// Update settings for an existing invite link
export const updateInvitationLink = async (
  inviteLinkId: string,
  updateableFields: UsersAPI.UpdateInviteLinkRequest,
): Promise<UsersAPI.InviteLinkDetails> => {
  try {
    const response = await axios.put(
      `/api/invite/invitation-link/${inviteLinkId}`,
      updateableFields,
      {
        headers: { Authorization: cookieStorage.getToken() },
      },
    );
    return handlingResponse<UsersAPI.InviteLinkDetails>(
      [200],
      "Error updating settings for an existing invite link",
    )(response);
  } catch (error) {
    const { message, status, errCode } = logError(error as AxiosError<ApiRequestError>);
    throw new RequestError({
      message,
      status,
      errCode,
      type: ERROR_TYPES.UPDATE_INVITATION_LINK_FAILED,
    });
  }
};

// Request to add new invitations to an existing email invite bundle
export const addInviteToEmailInvitationBundle = async (
  emailInviteBundleId: string,
  targetEmails: string[],
): Promise<UsersAPI.AddEmailInviteToBundleResponse> => {
  try {
    const response = await axios.post(
      `/api/invite/email-bundle/${emailInviteBundleId}/invite`,
      {
        targetEmails,
      },
      {
        headers: { Authorization: cookieStorage.getToken() },
      },
    );
    return handlingResponse<UsersAPI.AddEmailInviteToBundleResponse>(
      [200],
      "Error adding new invites to existing email invite bundle details",
    )(response);
  } catch (error) {
    const { message, status, errCode } = logError(error as AxiosError<ApiRequestError>);
    throw new RequestError({
      message,
      status,
      errCode,
      type: ERROR_TYPES.ADD_INVITE_TO_EXISTING_EMAIL_INVITE_BUNDLE,
    });
  }
};

// Fetch information on product license associated to an invite link
export const getInvitationLinkLicenseInfo = async (
  inviteLinkId: string,
): Promise<UsersAPI.InviteLinkLicenseInfo> => {
  try {
    const response = await axios.get(`/api/invite/invitation-link/${inviteLinkId}/license-info`, {
      headers: { Authorization: cookieStorage.getToken() },
    });
    return handlingResponse<UsersAPI.InviteLinkLicenseInfo>(
      [200],
      "Error fetching info on license associated to invite link",
    )(response);
  } catch (error) {
    const { message, status, errCode } = logError(error as AxiosError<ApiRequestError>);
    throw new RequestError({
      message,
      status,
      errCode,
      type: ERROR_TYPES.GET_INVITE_LINK_LICENSE_INFO,
    });
  }
};

// Claim product license associated to an invite link
export const claimInvitationLinkLicense = async (inviteLinkId: string): Promise<void> => {
  try {
    const response = await axios.post(
      `/api/invite/invitation-link/${inviteLinkId}/claim-license`,
      undefined,
      {
        headers: { Authorization: cookieStorage.getToken() },
      },
    );
    return handlingResponse<void>(
      [200],
      "Error claiming license associated to invite link",
    )(response);
  } catch (error) {
    const { message, status, errCode } = logError(error as AxiosError<ApiRequestError>);
    throw new RequestError({
      message,
      status,
      errCode,
      type: ERROR_TYPES.CLAIM_INVITE_LINK_LICENSE,
    });
  }
};
